import {Fragment, useCallback, useState} from 'react';

import styled from 'styled-components';
import {useSelector} from 'react-redux';

import {createLogger} from '~/shared/logging';
import {selectCheckoutPayments, selectOrderError, selectUserData} from '~/shared/store/selectors';
import {getCurrentLocation, pushRoute} from '~/shared/router';
import {routeNames} from '~/shared/routes';
import {flexColumn} from '~/shared/theme/FlexLayout';
import OtlSectionsContainer from '~/shared/components/Otl/OtlSectionsContainer';
import {useOnPageInit} from '~/shared/managers/initManager/initManager';
import store from '~/shared/store';
import {flipOnLTR, media} from '~/shared/theme/utils';
import {OtlMode} from '~/shared/components/Otl/types';
import {PieCloseModalButton} from '~/shared/components/Modals/styled';

const ModalRoot = styled.div<{isAddCreditCardMode?: boolean}>`
  position: relative;
  background-color: ${({theme}) => theme.loginModal.backgroundColor};
  border-radius: 2px;
  overflow: hidden;
  ${flexColumn}
  width: 100%;
  height: 100%;
  ${media.minLargeMobile`
    height: auto;
    && {
      width: 500px;
    }
  `}
`;

const StyledPieCloseModalButton = styled(PieCloseModalButton)`
  padding: 6px;
  top: 16px;
  ${flipOnLTR`
    left: 16px;
  `}
`;

const logger = createLogger('OtlPageComponent');

const pushRouteToCheckout = () => {
  pushRoute('/checkout');
};

const OtlPageComponent = ({closeModal, args}: {
  closeModal?: () => void;
  args?: {
    onSuccess?: (() => void) | (() => Promise<void>);
    onFailure?: (() => void) | (() => Promise<void>);
    onClose?: () => void;
    showBackArrow?: boolean;
    showUnlinkButton?: boolean;
  };
}) => {
  const orderError = useSelector(selectOrderError);
  const payments = useSelector(selectCheckoutPayments);
  const [isAddCreditCardMode, setIsCreditCardMode] = useState(false);
  const onModeChange = useCallback((nextMode: OtlMode) => {
    if (nextMode === OtlMode.ADD_CREDIT_CARD) {
      setIsCreditCardMode(true);
    } else {
      setIsCreditCardMode(false);
    }
  }, []);

  const encryptedMoneycardId = payments?.find(p =>
    p.cardId === orderError?.data.submitOrderFailurePaymentId,
  )?.encryptedMoneycardId;

  const isModal = !!closeModal;

  useOnPageInit(() => {
    const userData = selectUserData(store.getState());
    if (!userData) {
      if (isModal) {
        closeModal();
        logger.warn('Modal closed, no userData');
      } else {
        pushRoute(`/signin?returnUrl=${encodeURIComponent('/otl')}`);
        logger.warn('Redirecting to login and onSuccess should redirect back to the otl page');
      }
    }
  });

  const RootElement = closeModal ? ModalRoot : Fragment;

  const {onSuccess, onFailure, onClose} = args || {};

  const handleCheckout = () => {
    if (!closeModal) {
      pushRouteToCheckout();
    }
    const {routeName} = getCurrentLocation();
    if (closeModal && routeName === routeNames.checkout) {
      closeModal();
    }
  };

  const onCloseHandler = () => {
    if (onClose) {
      onClose();
    }
    handleCheckout();
  };

  const onSuccessHandler = () => {
    if (onSuccess) {
      onSuccess();
    }
    handleCheckout();
  };

  return (
    <RootElement isAddCreditCardMode={isAddCreditCardMode}>
      {closeModal && <StyledPieCloseModalButton onClick={closeModal} />}
      <OtlSectionsContainer
        orderCardId={encryptedMoneycardId}
        onClose={onCloseHandler}
        onOtlSuccess={onSuccessHandler}
        onOtlFailure={onFailure}
        onModeChange={onModeChange}
        showBackArrow={args?.showBackArrow}
        showUnlinkButton={args?.showUnlinkButton}
        onUnlinkSuccess={args?.onSuccess}
        showCloseButton={!closeModal}
      />
    </RootElement>
  );
};

export default OtlPageComponent;
